import { DataTable } from '@/components/dataTable';
import { useDataTable } from '@/components/dataTable/hooks/use-data-table';
import { DataTableToolbar } from '@/components/dataTable/ToolBar';
import { useDynamicLocale } from '@/hoslog-intl';
import { exportCsvSingleFilePapa } from '@/lib/exportCsv';
import { Pagination } from '@/react-query/context/ApiClientContextController';
import { StorePerformance } from '@/services/dashboard/conversionAnalytics/conversionAnalytics.types';
import * as React from 'react';
import { getColumns } from './Column';

interface StorePerformanceTableProps {
  data: StorePerformance[];
  isLoading: boolean;
  pagination: Pagination;
}

export function StorePerformanceTable({ data, isLoading, pagination }: StorePerformanceTableProps) {
  const { t } = useDynamicLocale();
  const columns = React.useMemo(() => getColumns(t), [t]);
  const pageCount = Math.ceil((pagination.totalItems ?? 0) / (pagination.pageSize || 1));

  const finalData = data.map((item, index) => ({
    SN: index + 1,
    [t('common.page.tableColumn.storeName')]: item.storeName,
    [t('common.page.tableColumn.reservationStore')]: item.reservationStore,
    [t('common.page.tableColumn.hostDetails')]: item.hostDetails,
    [t('common.page.tableColumn.phoneStore')]: item.phoneStore,
    [t('common.page.tableColumn.phoneHost')]: item.phoneHost,
    [t('common.page.tableColumn.totalClicks')]: item.totalClick,
  }));

  function handleExport() {
    exportCsvSingleFilePapa(finalData, {
      fileName: 'store-performance',
      format: 'YYYY/MM/DD',
    });
  }

  const { table } = useDataTable({
    data,
    columns,
    pageCount,
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 5,
      },
      columnVisibility: {
        createdDate: false,
      },
    },
    meta: {
      defaultDatePreset: 'today',
      title: t('dashboard.conversion.tableTitle.storePerformance'),
      totalPage: pagination.totalItems,
      pageSize: pagination.pageSize,
      getSerialNumber: (rowIndex: number) =>
        Math.max((pagination.currentPage ?? 1) - 1, 0) * (pagination.pageSize ?? 5) + rowIndex + 1,
      actionKey: 'exportCsv',
      action: handleExport,
      actionDisabled: data.length === 0,
      loading: isLoading,
    },
    getRowId: (row) => row.id ?? row.clubId,
    shallow: false,
    clearOnDefault: true,
  });

  return (
    <DataTable table={table}>
      <DataTableToolbar defaultDatePreset="today" />
    </DataTable>
  );
}
